import { MutableRefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  clearError,
  clearQueuedMessages,
  clearSuggestions,
  closeChat as closeChatAction,
  openChat as openChatAction,
  selectCurrentThreadId,
  setError,
  setGeneralSuggestions,
  setIsStreaming,
  setThreadId,
  useAppDispatch,
  useDeleteThreadMutation,
} from '@fly/store';
import log from 'loglevel';

interface UseThreadActionsParams {
  streamRef: MutableRefObject<{ close: () => void } | null>;
}

export const useThreadActions = ({ streamRef }: UseThreadActionsParams) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentThreadId = useSelector(selectCurrentThreadId);
  const [deleteThreadMutation] = useDeleteThreadMutation();

  const deleteThread = useCallback(
    async (threadId: string) => {
      const wasCurrent = currentThreadId === threadId;
      if (wasCurrent) {
        streamRef.current?.close();
        dispatch(setIsStreaming(false));
        dispatch(setThreadId(null));
        dispatch(clearError());
        dispatch(setGeneralSuggestions());
      }

      try {
        await deleteThreadMutation(threadId).unwrap();
        if (wasCurrent) {
          dispatch(clearQueuedMessages());
        }
      } catch (err) {
        log.error('Failed to delete thread:', err);
        if (wasCurrent) {
          dispatch(setThreadId(threadId));
        }
        dispatch(setError(t('app.dashboard.chat.errors.delete_thread')));
      }
    },
    [dispatch, currentThreadId, deleteThreadMutation, streamRef, t]
  );

  const closeChat = useCallback(() => {
    dispatch(closeChatAction());
  }, [dispatch]);

  const openChat = useCallback(() => {
    dispatch(openChatAction(true));
  }, [dispatch]);

  const newChat = useCallback(() => {
    streamRef.current?.close();
    dispatch(setIsStreaming(false));
    dispatch(setThreadId(null));
    dispatch(clearError());
    dispatch(clearQueuedMessages());
    dispatch(setGeneralSuggestions());
  }, [dispatch, streamRef]);

  const selectThread = useCallback(
    (threadId: string) => {
      if (threadId === currentThreadId) return;

      streamRef.current?.close();
      dispatch(setIsStreaming(false));
      dispatch(setThreadId(threadId));
      dispatch(clearError());
      dispatch(clearQueuedMessages());
      dispatch(clearSuggestions());
    },
    [currentThreadId, dispatch, streamRef]
  );

  return {
    newChat,
    selectThread,
    deleteThread,
    openChat,
    closeChat,
  };
};
